import { useState, useEffect, useCallback } from 'react';
import { responsesApiService } from '../services/ResponsesApiService';
import type { ResponsesMonitorState } from '../types/responses';

export const useResponsesMonitor = () => {
  const [state, setState] = useState<ResponsesMonitorState>({
    responses: [],
    loading: false,
    error: null,
    expandedItems: new Set<string>(),
  });

  const loadResponses = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const responses = await responsesApiService.getLatestResponses(10);
      setState(prev => ({ 
        ...prev, 
        responses, 
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error loading responses',
        loading: false 
      }));
    }
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setState(prev => {
      const newExpanded = new Set(prev.expandedItems);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      return { ...prev, expandedItems: newExpanded };
    });
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const refreshResponses = useCallback(() => {
    loadResponses();
  }, [loadResponses]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  return {
    responses: state.responses,
    loading: state.loading,
    error: state.error,
    expandedItems: state.expandedItems,
    actions: {
      refreshResponses,
      toggleExpanded,
      clearError,
    },
  };
};
